/**
 * Referral capture (client side).
 *
 * When someone arrives through a shared link (`<host>/?ref=CODE`), we stash
 * the code in localStorage so it survives the whole sign-up journey (landing
 * → auth popup → onboarding). It's then attached to the `completeProfile`
 * call at sign-up, which records the referral server-side. Cleared once
 * consumed so it can't leak into a later, unrelated account on the same
 * device.
 *
 * The stored code carries a capture timestamp and expires after 30 days: a
 * genuine recent referral still carries through the signup flow, but a code
 * from a link clicked weeks ago quietly lapses instead of claiming a referral
 * (and showing "a friend invited you") forever.
 */

const REF_KEY = 'entredash_ref';
/** How long a captured referral stays valid before it lapses. */
const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

interface StoredRef {
  code: string;
  ts: number; // unix ms when the code was captured
}

/** Referral codes are 4–16 chars from an unambiguous uppercase alphabet. */
function normalize(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const code = raw.trim().toUpperCase();
  return /^[A-Z0-9]{4,16}$/.test(code) ? code : null;
}

/**
 * Read and parse the stored referral, tolerating the legacy plain-string
 * format. A legacy value (no timestamp) is migrated to the new shape with a
 * fresh timestamp so an in-flight referral isn't dropped by this upgrade.
 * Returns null for absent/invalid entries.
 */
function readStored(): StoredRef | null {
  try {
    const raw = localStorage.getItem(REF_KEY);
    if (!raw) return null;

    // New format: JSON { code, ts }.
    if (raw.charAt(0) === '{') {
      const obj = JSON.parse(raw) as { code?: unknown; ts?: unknown };
      const code = normalize(typeof obj.code === 'string' ? obj.code : null);
      if (!code) return null;
      const ts = typeof obj.ts === 'number' ? obj.ts : 0;
      return { code, ts };
    }

    // Legacy plain-string code — migrate with a fresh timestamp.
    const code = normalize(raw);
    if (!code) return null;
    const migrated: StoredRef = { code, ts: Date.now() };
    localStorage.setItem(REF_KEY, JSON.stringify(migrated));
    return migrated;
  } catch {
    return null;
  }
}

/**
 * Read `?ref=` from the current URL and persist it. Safe to call on every
 * load; only writes when a valid code is present. First still-valid link
 * wins (matching the backend guarantee), but a lapsed/expired stored code is
 * replaced by a fresh capture. Call once near app start.
 */
export function captureReferralFromUrl(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    const code = normalize(params.get('ref'));
    if (!code) return;
    // Only defer to an existing code if it's still within its lifespan;
    // getStoredReferralCode() clears it if expired.
    if (getStoredReferralCode()) return;
    localStorage.setItem(REF_KEY, JSON.stringify({ code, ts: Date.now() } satisfies StoredRef));
  } catch {
    // Ignore — referral capture is best-effort.
  }
}

/** The stored referral code, if any and not yet expired. Clears it when expired. */
export function getStoredReferralCode(): string | null {
  const stored = readStored();
  if (!stored) return null;
  if (Date.now() - stored.ts > TTL_MS) {
    clearStoredReferralCode();
    return null;
  }
  return stored.code;
}

/** Clear the stored code once it's been consumed at sign-up (or on expiry). */
export function clearStoredReferralCode(): void {
  try {
    localStorage.removeItem(REF_KEY);
  } catch {
    // Ignore.
  }
}
